import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FileText, Search, ArrowRight, Clock, ChevronRight,
    Building2, Tag, FormInput, Users, GitBranch,
    TriangleAlert, BarChart3,
    MessageCircle, CheckCircle, UserCheck, Send, MessageSquare,
    Layers, Hash, Star, ToggleLeft, Upload
} from 'lucide-react';
import LandingGraphicPng from '@Assets/landingGraphic.png';
import AppScreenLanding from '@Assets/phone-app-designs/landing.jpeg';
import AppScreenTicketList from '@Assets/phone-app-designs/ticket_list.jpeg';
import AppScreenTicketCreate from '@Assets/phone-app-designs/ticket_creation.jpeg';
import AppScreenTracking from '@Assets/phone-app-designs/tracking_ticket_history.jpeg';
import { ReactComponent as WhatsAppIcon } from '@Assets/whatsAppDigitalGlyphGreen.svg';
import { FloatingPaths, AnimatedWord } from '@LandingFunctionalDisplay/BackgroundPaths';
import PaperPlaneBlue from '@Assets/paper_plane_blue.webp';
import PaperPlaneGreen from '@Assets/paper_plane_green.webp';
import PaperPlanePink from '@Assets/paper_plane_pink.webp';
import PaperPlanePurple from '@Assets/paper_plane_purple.webp';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import { useRecentlyVisited } from '@Hooks/useRecentlyVisited';
import useSEO from '@Hooks/useSEO';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './LandingPage.scss';

// ─── Scroll-reveal wrapper ────────────────────────────────────────────────────
function Reveal({ children, delay = 0, y = 28, className = '' }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay, ease: 'easeOut' }}
        >
            {children}
        </motion.div>
    );
}

export default function LandingPage() {

    const navigate  = useNavigate();
    const { activeUser } = useReduxActiveUser();
    const { getRecentForms, getRecentTickets } = useRecentlyVisited();
    const [recentItems, setRecentItems] = useState([]);
    const className = 'landingPage';

    useEffect(() => {
        const forms   = getRecentForms().map(f => ({ ...f, type: 'form',   path: '/report/' + f.formId,              label: f.title || f.formId }));
        const tickets = getRecentTickets().map(t => ({ ...t, type: 'ticket', path: '/home/track?ticketId=' + t.ticketId, label: t.title || t.ticketId }));
        const combined = [...forms, ...tickets].sort((a, b) => b.timestamp - a.timestamp).slice(0, 4);
        setRecentItems(combined);
    }, []);

    const isLoggedIn = !!activeUser && Object.keys(activeUser).length > 0 && activeUser?.id > 0;

    const seo = useSEO({
        title:       'No-Code Ticket Management with WhatsApp Notifications',
        description: 'KaizIQ Resolve helps teams manage support tickets with no-code forms, WhatsApp notifications, SLA enforcement, and a white-label mobile app. Start free.',
        canonical:   'https://resolve.kaiziq.io/',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'WebPage',
            name:       'KaizIQ Resolve — Home',
            url:        'https://resolve.kaiziq.io/',
            description: 'No-code ticket management platform with WhatsApp notifications and SLA enforcement.',
            breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://resolve.kaiziq.io/' }] },
        },
    });

    const handleTrackClick = () => {
        if (!isLoggedIn) {
            navigate('/login', { state: { from: { pathname: '/home/track' } } });
        } else {
            navigate('/home/track');
        }
    };

    // ── Data ────────────────────────────────────────────────────────────────

    const stats = [
        { value: '20,000+', label: 'Tickets Reported' },
        { value: '2,000+',  label: 'Satisfied Users'  },
        { value: '70%',     label: 'Faster Response'  },
        { value: '25+',     label: 'Enterprise Clients'},
    ];

    const setupSteps = [
        {
            num: '01', icon: <Building2 size={22}/>,
            title: 'Add Your Organisation',
            desc: 'Register your organisation name and set a unique tag (e.g. APCKZQ) that will be prefixed to every ticket and form you create.',
        },
        {
            num: '02', icon: <FormInput size={22}/>,
            title: 'Build Your First Form',
            desc: 'Create purpose-specific forms for different use cases — maintenance, support, onboarding — each with its own fields, agents, and automation rules.',
        },
        {
            num: '03', icon: <Send size={22}/>,
            title: 'Share & Go Live',
            desc: 'Share the public form URL with your clients. Tickets start flowing in immediately, with WhatsApp and Email notifications going out automatically.',
        },
    ];

    const fieldTypes = [
        { icon: <FileText size={16}/>,   label: 'Text'       },
        { icon: <Hash size={16}/>,       label: 'Number'     },
        { icon: <Upload size={16}/>,     label: 'File'       },
        { icon: <Star size={16}/>,       label: 'Rating'     },
        { icon: <ToggleLeft size={16}/>, label: 'Select'     },
        { icon: <Layers size={16}/>,     label: 'Multi-page' },
    ];

    const formFeatures = [
        {
            icon: <Users size={22}/>, accent: '#67d2df',
            title: 'Agent Management',
            desc: 'Add agents who receive real-time notifications on ticket creation. Agents interact with clients on behalf of your organisation and can forward tickets to the right teams — Engineering, Sales, Support, and more.',
        },
        {
            icon: <UserCheck size={22}/>, accent: '#86efac',
            title: 'Auto-Assignee Matrix',
            desc: 'Define rules that automatically assign tickets to specific people based on field responses. No manual triage — the right person is notified the moment a ticket arrives.',
        },
        {
            icon: <TriangleAlert size={22}/>, accent: '#fca5a5',
            title: 'Priority & Escalation Matrix',
            desc: 'Auto-assign ticket priorities based on submitted field values. Configure escalation paths so high-priority issues are automatically surfaced to the right stakeholders on time.',
        },
        {
            icon: <Tag size={22}/>, accent: '#c4b5fd',
            title: 'Smart Label Matrix',
            desc: 'Automatically apply Organisation and Contact labels based on the reporter\'s email, name, or phone number. Drive richer analytics and segmentation without any manual work.',
        },
        {
            icon: <BarChart3 size={22}/>, accent: '#fbbf24',
            title: 'Reports & Analytics',
            desc: 'Export detailed ticket reports in Excel and PDF. Track team performance, resolution times, and ticket trends across all your forms and agents.',
        },
        {
            icon: <GitBranch size={22}/>, accent: '#a5f3fc',
            title: 'Conditional Navigation',
            desc: 'Create multi-page forms with smart branching logic. Each page can dynamically show or skip fields based on the user\'s previous answers, for a clean reporting experience.',
        },
    ];

    const whatsappFlow = [
        {
            icon: <MessageCircle size={18}/>, side: 'out',
            title: 'Ticket Reported',
            msg: 'New ticket raised: [APCKZQ-0042] — AI summary of the issue is sent to the reporter and all assigned agents instantly.',
        },
        {
            icon: <UserCheck size={18}/>, side: 'in',
            title: 'Auto / Manual Assignment',
            msg: 'The assignee matrix routes the ticket to the right team. A WhatsApp message notifies the assignee with full context.',
        },
        {
            icon: <MessageSquare size={18}/>, side: 'out',
            title: 'Two-Way Commenting',
            msg: 'The reporter comments directly on the WhatsApp thread. Agents and assignees reply via the web app — keeping clients in the loop without granting system access.',
        },
        {
            icon: <CheckCircle size={18}/>, side: 'in',
            title: 'Resolved',
            msg: 'Mark as resolved directly from WhatsApp. A confirmation is sent to the reporter, agents, and stakeholders. Email confirmation goes out simultaneously.',
        },
    ];

    const clients = [
        'Escorts', 'NYK', 'RSBPL', 'Trihun', 'Hero',
        'Polyplex', 'Max Healthcare',
    ];

    const appScreens = [
        { src: AppScreenLanding,      alt: 'KaizIQ Desk splash screen'      },
        { src: AppScreenTicketList,   alt: 'Ticket list view'                },
        { src: AppScreenTicketCreate, alt: 'Ticket creation form'            },
        { src: AppScreenTracking,     alt: 'Ticket tracking and history'     },
    ];

    const decorationSrcs = [PaperPlaneBlue, PaperPlaneGreen, PaperPlanePink, PaperPlanePurple, PaperPlaneGreen, PaperPlanePink, PaperPlanePurple];

    return (
        <div className={className}>
            {seo}
            <TopPanel logo={false}/>

            <div className={className + '__container'}>

                {/* ─── Hero ──────────────────────────────────────────── */}
                <section className={className + '__hero'}>
                    <FloatingPaths position={1}  />
                    <FloatingPaths position={-1} />

                    <div className={className + '__hero__left'}>
                        <motion.div
                            className={className + '__hero__badge'}
                            initial={{ opacity: 0, y: -16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <span>🚀 Powered by KaizIQ</span>
                        </motion.div>
                        <h1 className={className + '__hero__title'}>
                            <AnimatedWord word="Resolve" delay={0.1} className={className + '__hero__title__main'} />
                            <motion.span
                                className={className + '__hero__title__sub'}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.55, duration: 0.55, ease: 'easeOut' }}
                            >
                                Smart Ticket Management
                            </motion.span>
                        </h1>
                        <motion.p
                            className={className + '__hero__desc'}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.75, duration: 0.55, ease: 'easeOut' }}
                        >
                            End-to-end ticket management with automated WhatsApp, Email and App
                            notifications — built for enterprises that demand zero follow-up overhead.
                        </motion.p>
                        <motion.ul
                            className={className + '__hero__features'}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.95, duration: 0.5, ease: 'easeOut' }}
                        >
                            {['Custom Forms', 'WhatsApp Alerts', 'Auto-Assignment', 'AI Summaries'].map(f => (
                                <li key={f} className={className + '__hero__features__item'}>
                                    <span className={className + '__hero__features__item__check'}>✓</span>
                                    <span>{f}</span>
                                </li>
                            ))}
                        </motion.ul>
                    </div>

                    <motion.div
                        className={className + '__hero__right'}
                        initial={{ opacity: 0, x: 32 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.65, ease: 'easeOut' }}
                    >
                        <div
                            className={className + '__action-card ' + className + '__action-card--report'}
                            onClick={() => navigate('/home/form/search')}
                        >
                            <div className={className + '__action-card__icon-wrap'}>
                                <FileText size={26} />
                            </div>
                            <div className={className + '__action-card__body'}>
                                <h3 className={className + '__action-card__title'}>Report a Ticket</h3>
                                <p className={className + '__action-card__desc'}>
                                    Submit a new issue using an organisation's form
                                </p>
                            </div>
                            <ArrowRight className={className + '__action-card__arrow'} size={20} />
                        </div>

                        <div
                            className={className + '__action-card ' + className + '__action-card--track'}
                            onClick={handleTrackClick}
                        >
                            <div className={className + '__action-card__icon-wrap'}>
                                <Search size={26} />
                            </div>
                            <div className={className + '__action-card__body'}>
                                <h3 className={className + '__action-card__title'}>Track Your Ticket</h3>
                                <p className={className + '__action-card__desc'}>
                                    Check the live status of tickets you've submitted
                                </p>
                            </div>
                            <ArrowRight className={className + '__action-card__arrow'} size={20} />
                        </div>

                        {recentItems.length > 0 && (
                            <div className={className + '__recent'}>
                                <div className={className + '__recent__header'}>
                                    <Clock size={13} />
                                    <span>Recently Visited</span>
                                </div>
                                <div className={className + '__recent__list'}>
                                    {recentItems.map((item, i) => (
                                        <div key={i} className={className + '__recent__item'} onClick={() => navigate(item.path)}>
                                            <span className={className + '__recent__item__badge' + (item.type === 'ticket' ? ' ' + className + '__recent__item__badge--ticket' : '')}>
                                                {item.type === 'form' ? 'Form' : 'Ticket'}
                                            </span>
                                            <span className={className + '__recent__item__label'}>{item.label}</span>
                                            <ChevronRight size={12} className={className + '__recent__item__arrow'} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                </section>

                {/* ─── Stats ─────────────────────────────────────────── */}
                <section className={className + '__stats'}>
                    {stats.map((s, i) => (
                        <Reveal key={i} delay={i * 0.08}>
                            <div className={className + '__stats__item'}>
                                <div className={className + '__stats__item__value'}>{s.value}</div>
                                <div className={className + '__stats__item__label'}>{s.label}</div>
                            </div>
                        </Reveal>
                    ))}
                </section>

                {/* ─── Dashboard Preview ────────────────────────────── */}
                <Reveal className={className + '__preview'}>
                    <div className={className + '__preview__label'}>
                        <span>✦</span>
                        <span>See Resolve in action</span>
                        <span>✦</span>
                    </div>

                    <div className={className + '__preview__wrap'}>
                        {/* Floating WhatsApp bubbles */}
                        <motion.div
                            className={className + '__preview__bubble ' + className + '__preview__bubble--1'}
                            initial={{ opacity: 0, y: 16, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.5, ease: 'easeOut' }}
                            animate={{ y: [0, -6, 0] }}
                        >
                            <WhatsAppIcon className={className + '__preview__bubble__logo'} />
                            <div className={className + '__preview__bubble__body'}>
                                <div className={className + '__preview__bubble__title'}>New Ticket</div>
                                <div className={className + '__preview__bubble__text'}>APCKZQ-0042 has been raised and assigned to you.</div>
                            </div>
                        </motion.div>

                        <motion.div
                            className={className + '__preview__bubble ' + className + '__preview__bubble--2'}
                            initial={{ opacity: 0, y: 16, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6, duration: 0.5, ease: 'easeOut' }}
                            animate={{ y: [0, -8, 0] }}
                        >
                            <WhatsAppIcon className={className + '__preview__bubble__logo'} />
                            <div className={className + '__preview__bubble__body'}>
                                <div className={className + '__preview__bubble__title'}>Resolved ✓</div>
                                <div className={className + '__preview__bubble__text'}>Your ticket has been resolved. Please confirm.</div>
                            </div>
                        </motion.div>

                        <motion.div
                            className={className + '__preview__bubble ' + className + '__preview__bubble--3'}
                            initial={{ opacity: 0, y: 16, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.55, duration: 0.5, ease: 'easeOut' }}
                            animate={{ y: [0, -5, 0] }}
                        >
                            <WhatsAppIcon className={className + '__preview__bubble__logo'} />
                            <div className={className + '__preview__bubble__body'}>
                                <div className={className + '__preview__bubble__title'}>Team Update</div>
                                <div className={className + '__preview__bubble__text'}>Assigned to Engineering team.</div>
                            </div>
                        </motion.div>

                        <div className={className + '__preview__frame'}>
                            <div className={className + '__preview__frame__bar'}>
                                <span/><span/><span/>
                            </div>
                            <img
                                src={LandingGraphicPng}
                                alt='Resolve dashboard preview'
                                className={className + '__preview__img'}
                            />
                        </div>
                    </div>
                </Reveal>

                {/* ─── Setup Journey ─────────────────────────────────── */}
                <section className={className + '__setup'}>
                    <Reveal>
                        <div className={className + '__section-header'}>
                            <div className={className + '__section-tag'}>Getting Started</div>
                            <h2>Up and running in minutes</h2>
                            <p>Three steps from sign-up to your first live form</p>
                        </div>
                    </Reveal>
                    <div className={className + '__setup__grid'}>
                        {setupSteps.map((step, i) => (
                            <Reveal key={i} delay={i * 0.1}>
                                <div className={className + '__setup__card'}>
                                    <div className={className + '__setup__card__top'}>
                                        <div className={className + '__setup__card__icon'}>{step.icon}</div>
                                        <div className={className + '__setup__card__num'}>{step.num}</div>
                                    </div>
                                    <h3 className={className + '__setup__card__title'}>{step.title}</h3>
                                    <p className={className + '__setup__card__desc'}>{step.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* ─── Form Builder ──────────────────────────────────── */}
                <section className={className + '__features'}>
                    <Reveal>
                        <div className={className + '__section-header'}>
                            <div className={className + '__section-tag'}>Form Engine</div>
                            <h2>Everything your form needs, built in</h2>
                            <p>From simple single-page forms to complex multi-step branching flows</p>
                        </div>
                    </Reveal>

                    {/* Field type pills */}
                    <Reveal delay={0.1}>
                        <div className={className + '__fieldtypes'}>
                            <span className={className + '__fieldtypes__label'}>Supported field types</span>
                            <div className={className + '__fieldtypes__list'}>
                                {fieldTypes.map((f, i) => (
                                    <span key={i} className={className + '__fieldtypes__pill'}>
                                        {f.icon}{f.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>

                    <div className={className + '__features__grid'}>
                        {formFeatures.map((feat, i) => (
                            <Reveal key={i} delay={(i % 3) * 0.1}>
                                <div className={className + '__features__card'}>
                                    <div className={className + '__features__card__icon'} style={{ color: feat.accent, background: feat.accent + '22' }}>
                                        {feat.icon}
                                    </div>
                                    <h3 className={className + '__features__card__title'}>{feat.title}</h3>
                                    <p className={className + '__features__card__desc'}>{feat.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* ─── WhatsApp Integration ──────────────────────────── */}
                <section className={className + '__whatsapp'}>
                    <div className={className + '__whatsapp__inner'}>
                        <Reveal className={className + '__whatsapp__left'}>
                            <div className={className + '__section-tag ' + className + '__section-tag--green'}>WhatsApp Native</div>
                            <h2 className={className + '__whatsapp__title'}>
                                Your entire ticket lifecycle<br />
                                <span>on WhatsApp</span>
                            </h2>
                            <p className={className + '__whatsapp__subtitle'}>
                                Every key event — ticket created, assigned, commented, resolved —
                                triggers a real WhatsApp message. No app install required for your clients.
                            </p>
                        </Reveal>

                        <div className={className + '__whatsapp__thread'}>
                            {whatsappFlow.map((msg, i) => (
                                <Reveal key={i} delay={i * 0.12}>
                                    <div className={className + '__whatsapp__msg ' + className + '__whatsapp__msg--' + msg.side}>
                                        <div className={className + '__whatsapp__msg__icon'}>{msg.icon}</div>
                                        <div className={className + '__whatsapp__msg__body'}>
                                            <div className={className + '__whatsapp__msg__title'}>{msg.title}</div>
                                            <div className={className + '__whatsapp__msg__text'}>{msg.msg}</div>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── Trusted By ────────────────────────────────────── */}
                <section className={className + '__clients'}>
                    <Reveal>
                        <div className={className + '__section-header'}>
                            <div className={className + '__section-tag'}>Trusted By</div>
                            <h2>Enterprises that rely on Resolve</h2>
                            <p>Across manufacturing, logistics, healthcare, and more</p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className={className + '__clients__strip'}>
                            {clients.map((name, i) => (
                                <div key={i} className={className + '__clients__chip'}>{name}</div>
                            ))}
                            <div className={className + '__clients__chip ' + className + '__clients__chip--more'}>
                                +20 more
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* ─── Mobile App Showcase ───────────────────────────── */}
                <section className={className + '__mobile'}>
                    <div className={className + '__mobile__inner'}>
                        <Reveal className={className + '__mobile__left'}>
                            <div className={className + '__section-tag ' + className + '__section-tag--cyan'}>Now Live</div>
                            <h2 className={className + '__mobile__title'}>
                                KaizIQ Desk<br />
                                <span>White-Label Mobile App</span>
                            </h2>
                            <p className={className + '__mobile__desc'}>
                                A fully branded Android &amp; iOS app deployed under your organisation's
                                name and logo — available on the Play Store and App Store. Your clients
                                can report, update, and track tickets directly from their phones.
                            </p>
                            <ul className={className + '__mobile__perks'}>
                                {[
                                    'Report tickets from anywhere',
                                    'Real-time status updates',
                                    'Full ticket history',
                                    'Your brand, your logo',
                                ].map(p => (
                                    <li key={p} className={className + '__mobile__perks__item'}>
                                        <span className={className + '__mobile__perks__check'}>✓</span>
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>

                        <div className={className + '__mobile__screens'}>
                            {appScreens.map((screen, i) => (
                                <motion.div
                                    key={i}
                                    className={className + '__mobile__phone ' + className + '__mobile__phone--' + (i + 1)}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1, duration: 0.55, ease: 'easeOut' }}
                                >
                                    <div className={className + '__mobile__phone__shell'}>
                                        <img src={screen.src} alt={screen.alt} className={className + '__mobile__phone__img'} />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* Floating decorations */}
            <div className={className + '__decorations'}>
                {decorationSrcs.map((src, i) => (
                    <img key={i} src={src} alt=''
                        className={className + '__decoration ' + className + '__decoration--' + (i + 1)} />
                ))}
            </div>

            <Footer />
        </div>
    );
}
